import type { Order } from '../../../../../types/apiResponse/order';

export type OrderCoordinates = {
  latitude: number;
  longitude: number;
};

export type ReverseGeocodeAddress = {
  name?: string;
  road?: string;
  houseNumber?: string;
  neighbourhood?: string;
  suburb?: string;
  city?: string;
  state?: string;
  postcode?: string;
  displayName?: string;
};

export const getOpenStreetMapUrl = ({ latitude, longitude }: OrderCoordinates) => {
  const url = new URL('https://www.openstreetmap.org/');
  url.searchParams.set('mlat', String(latitude));
  url.searchParams.set('mlon', String(longitude));
  url.hash = `map=18/${latitude}/${longitude}`;
  return url.toString();
};

export const getOpenStreetMapEmbedUrl = ({ latitude, longitude }: OrderCoordinates) => {
  const latitudeSpan = 0.008;
  const longitudeSpan = latitudeSpan / Math.max(Math.cos((latitude * Math.PI) / 180), 0.25);
  const south = Math.max(-85.05113, latitude - latitudeSpan);
  const north = Math.min(85.05113, latitude + latitudeSpan);
  const west = Math.max(-180, longitude - longitudeSpan);
  const east = Math.min(180, longitude + longitudeSpan);
  const url = new URL('https://www.openstreetmap.org/export/embed.html');

  url.searchParams.set('bbox', [west, south, east, north].join(','));
  url.searchParams.set('layer', 'mapnik');
  url.searchParams.set('marker', `${latitude},${longitude}`);
  return url.toString();
};

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/reverse';
const CACHE_PREFIX = 'order-location-address:v1:';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const MIN_REQUEST_INTERVAL_MS = 1100;

let nominatimQueue: Promise<unknown> = Promise.resolve();
let lastNominatimRequestAt = 0;

const parseCoordinate = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getOrderCoordinates = (
  order: Pick<Order, 'location_latitude' | 'location_longitude'>,
): OrderCoordinates | null => {
  const latitude = parseCoordinate(order.location_latitude);
  const longitude = parseCoordinate(order.location_longitude);

  if (
    latitude === null
    || longitude === null
    || latitude < -90
    || latitude > 90
    || longitude < -180
    || longitude > 180
  ) {
    return null;
  }

  return { latitude, longitude };
};

const cacheKey = ({ latitude, longitude }: OrderCoordinates) => (
  `${CACHE_PREFIX}${latitude.toFixed(7)}:${longitude.toFixed(7)}`
);

const readCachedAddress = (key: string): ReverseGeocodeAddress | null => {
  try {
    const cached = sessionStorage.getItem(key);
    if (!cached) return null;

    const entry = JSON.parse(cached) as { expiresAt?: number; address?: ReverseGeocodeAddress };
    if (!entry.expiresAt || entry.expiresAt <= Date.now() || !entry.address) {
      sessionStorage.removeItem(key);
      return null;
    }

    return entry.address;
  } catch {
    return null;
  }
};

const cacheAddress = (key: string, address: ReverseGeocodeAddress) => {
  try {
    sessionStorage.setItem(key, JSON.stringify({
      expiresAt: Date.now() + CACHE_TTL_MS,
      address,
    }));
  } catch {
    // Storage can be disabled or full; address lookup must keep working.
  }
};

const queueNominatimRequest = (url: string, signal: AbortSignal): Promise<Response> => {
  const request = nominatimQueue.then(async () => {
    if (signal.aborted) throw new Error('Address lookup cancelled');

    const waitMs = lastNominatimRequestAt + MIN_REQUEST_INTERVAL_MS - Date.now();
    if (waitMs > 0) {
      await new Promise((resolve) => window.setTimeout(resolve, waitMs));
    }

    if (signal.aborted) throw new Error('Address lookup cancelled');
    lastNominatimRequestAt = Date.now();

    return fetch(url, {
      headers: { 'Accept-Language': 'pt-BR,pt;q=0.9' },
      signal,
    });
  });

  nominatimQueue = request.then(() => undefined, () => undefined);
  return request;
};

const asText = (value: unknown) => (typeof value === 'string' ? value.trim() : '');

const parseReverseGeocodeAddress = (value: unknown): ReverseGeocodeAddress | null => {
  if (!value || typeof value !== 'object') return null;

  const result = value as {
    name?: unknown;
    display_name?: unknown;
    address?: Record<string, unknown>;
  };
  const address = result.address ?? {};
  const city = [address.city, address.town, address.village, address.municipality, address.hamlet]
    .map(asText)
    .find(Boolean);
  const parsed: ReverseGeocodeAddress = {
    name: asText(result.name) || undefined,
    road: [address.road, address.pedestrian, address.residential, address.footway]
      .map(asText)
      .find(Boolean),
    houseNumber: asText(address.house_number) || undefined,
    neighbourhood: asText(address.neighbourhood) || undefined,
    suburb: asText(address.suburb) || undefined,
    city,
    state: asText(address.state) || undefined,
    postcode: asText(address.postcode) || undefined,
    displayName: asText(result.display_name) || undefined,
  };

  return Object.values(parsed).some(Boolean) ? parsed : null;
};

export const getReverseGeocodeAddress = async (
  coordinates: OrderCoordinates,
  signal: AbortSignal,
): Promise<ReverseGeocodeAddress | null> => {
  const key = cacheKey(coordinates);
  const cachedAddress = readCachedAddress(key);
  if (cachedAddress) return cachedAddress;

  const url = new URL(NOMINATIM_URL);
  url.searchParams.set('lat', String(coordinates.latitude));
  url.searchParams.set('lon', String(coordinates.longitude));
  url.searchParams.set('format', 'jsonv2');
  url.searchParams.set('addressdetails', '1');
  url.searchParams.set('zoom', '18');

  const response = await queueNominatimRequest(url.toString(), signal);
  if (!response.ok) throw new Error(`Address lookup failed with status ${response.status}`);

  const address = parseReverseGeocodeAddress(await response.json());
  if (address) cacheAddress(key, address);
  return address;
};
